/**
 * Media Upload Utility (Hybrid Approach)
 *
 * This utility provides functions for:
 * - Uploading media files to the server (development)
 * - Falling back to client-side blob storage (production)
 * - Generating thumbnails
 * - Getting media duration
 */

import { ConvexReactClient } from "convex/react";
import { getUserId } from "./user-id";
import { UserMediaItem, addMediaItem, updateMediaItem } from "./indexdb";
import { MediaBunnyThumbnailService } from "../../services/media-bunny-thumbnail-service";
import { getSrcDuration } from "../../hooks/use-src-duration";
import { ConvexMediaService } from "../../services/convex-media-service";
import { computeFileHash } from "./file-hash";
import { MediaUrlUpdateService } from "../../services/media-url-update-service";

/**
 * Uploads a file with hybrid approach: immediate IndexedDB storage + background Convex upload
 *
 * Flow:
 * 1. Store in IndexedDB immediately with blob URL (fast UI)
 * 2. Upload to Convex in background (with deduplication)
 * 3. Update IndexedDB with Convex URL on success
 * 4. Fall back to blob URL on failure (graceful degradation)
 *
 * @param file - File to upload
 * @param convexClient - Authenticated Convex client (from useConvex hook)
 */
export const uploadMediaFile = async (
  file: File,
  convexClient?: ConvexReactClient
): Promise<UserMediaItem> => {
  try {
    // Step 1: Fast operations first (thumbnail, duration)
    const thumbnail = await generateThumbnail(file);
    let duration: number | undefined;

    // Only try to get duration for video and audio files, not images
    if (file.type.startsWith("video/") || file.type.startsWith("audio/")) {
      try {
        const result = await getSrcDuration(file);
        duration = result.durationInSeconds;
      } catch (error) {
        console.warn("Failed to get duration with media-parser, falling back to legacy method:", error);
        duration = await getMediaDuration(file);
      }
    } else {
      // Images don't have duration, set to undefined
      duration = undefined;
    }

    // Determine file type
    const fileType = getFileType(file);

    // Get user ID
    const userId = getUserId();

    // Step 2: Create blob URL for immediate use
    const fileBlob = new Blob([file], { type: file.type });
    const blobUrl = URL.createObjectURL(fileBlob);
    const fileId = crypto.randomUUID();

    // Step 3: Store in IndexedDB immediately with pending status
    const mediaItem: UserMediaItem = {
      id: fileId,
      userId,
      name: file.name,
      type: fileType,
      serverPath: blobUrl, // Blob URL for immediate use
      size: file.size,
      lastModified: file.lastModified,
      thumbnail: thumbnail || "",
      duration: duration || 0,
      createdAt: Date.now(),
      fileBlob, // Store blob for offline access
      uploadStatus: "pending", // Track upload status
    };

    await addMediaItem(mediaItem);

    // Step 4: Background upload to Convex (non-blocking)
    if (convexClient) {
      uploadToConvexBackground(file, fileId, userId, thumbnail, duration, convexClient, blobUrl).catch(
        (error) => {
          // Errors are handled inside uploadToConvexBackground
          console.error("[Upload] Background upload failed:", error);
        }
      );
    } else {
      console.warn("[Upload] No Convex client provided, skipping Convex upload");
      // Mark as failed if no client
      await updateMediaItem(fileId, {
        uploadStatus: "failed",
        uploadError: "No Convex client available",
      });
    }

    return mediaItem;
  } catch (error) {
    console.error("Error uploading media file:", error);
    throw error;
  }
};

/**
 * Background upload to Convex with deduplication
 * Notifies MediaUrlUpdateService when upload completes so overlays can update their URLs
 */
async function uploadToConvexBackground(
  file: File,
  fileId: string,
  userId: string,
  thumbnail: string,
  duration: number | undefined,
  convexClient: ConvexReactClient,
  blobUrl: string // Original blob URL for notification
): Promise<void> {
  try {
    console.log(`[Upload] Starting background upload for ${file.name}`);

    // Update status to uploading
    await updateMediaItem(fileId, { uploadStatus: "uploading" });
    console.log(`[Upload] Status updated to 'uploading'`);

    // Calculate file hash for deduplication
    console.log(`[Upload] Computing file hash...`);
    const fileHash = await computeFileHash(file);
    console.log(`[Upload] Hash computed: ${fileHash.substring(0, 16)}...`);

    // Upload to Convex (with deduplication check)
    console.log(`[Upload] Starting Convex upload...`);
    const result = await ConvexMediaService.uploadFile(
      convexClient,
      file,
      fileHash,
      thumbnail,
      duration
    );

    console.log(`[Upload] Convex upload complete, updating IndexedDB...`);

    // Success: Update IndexedDB with Convex data
    const updateSuccess = await updateMediaItem(fileId, {
      convexStorageId: result.storageId,
      convexUrl: result.url,
      fileHash,
      uploadStatus: "completed",
      lastSyncedAt: Date.now(),
    });

    console.log(`[Upload] IndexedDB update success: ${updateSuccess}`);
    console.log(
      `[Upload] ✓ Upload complete: ${file.name}, URL: ${result.url}`
    );

    // Notify MediaUrlUpdateService so overlays can update their URLs
    // This triggers the preload + URL switch flow for smooth transition
    MediaUrlUpdateService.notifyUploadComplete(fileId, blobUrl, result.url, file.name);
  } catch (error) {
    // Failure: Keep blob URL, mark as failed
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`[Upload] ✗ Upload failed for ${file.name}:`, errorMessage);
    console.error(`[Upload] Full error:`, error);

    try {
      await updateMediaItem(fileId, {
        uploadStatus: "failed",
        uploadError: errorMessage,
      });
      console.log(`[Upload] Status updated to 'failed' in IndexedDB`);
    } catch (updateError) {
      console.error(`[Upload] Failed to update status to failed:`, updateError);
    }
  }
}

/**
 * Get file type from File object
 */
function getFileType(file: File): "video" | "image" | "audio" {
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("audio/")) return "audio";
  throw new Error(`Unsupported file type: ${file.type}`);
}


/**
 * Generates a thumbnail for image or video files using MediaBunny for videos
 */
export const generateThumbnail = async (file: File): Promise<string> => {
  if (file.type.startsWith("image/")) {
    // For images, use FileReader as before
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve((e.target?.result as string) || "");
      };
      reader.onerror = () => {
        console.error("Error reading image file");
        resolve("");
      };
      reader.readAsDataURL(file);
    });
  } else if (file.type.startsWith("video/")) {
    // For videos, use MediaBunny for more reliable thumbnail generation
    try {
      const thumbnail = await MediaBunnyThumbnailService.generateThumbnail(file, {
        width: 320,
        height: 180,
        timestampSeconds: 1, // Capture at 1 second
        quality: 0.8,
      });
      return thumbnail;
    } catch (error) {
      console.error("MediaBunny thumbnail generation failed, falling back to canvas method:", error);
      
      // Fallback to the old canvas method if MediaBunny fails
      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";

        // Set timeout to handle cases where video loading hangs
        const timeoutId = setTimeout(() => {
          console.warn("Video thumbnail generation timed out");
          resolve("");
        }, 5000); // 5 second timeout

        video.onloadedmetadata = () => {
          // Set the time to 1 second or the middle of the video
          video.currentTime = Math.min(1, video.duration / 2);
        };

        video.onloadeddata = () => {
          clearTimeout(timeoutId);
          try {
            const canvas = document.createElement("canvas");
            canvas.width = 320;
            canvas.height = 180;
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnail = canvas.toDataURL("image/jpeg");
            resolve(thumbnail);
          } catch (error) {
            console.error("Error generating video thumbnail:", error);
            resolve("");
          } finally {
            URL.revokeObjectURL(video.src);
          }
        };

        video.onerror = () => {
          clearTimeout(timeoutId);
          console.error("Error loading video for thumbnail");
          URL.revokeObjectURL(video.src);
          resolve("");
        };

        video.src = URL.createObjectURL(file);
      });
    }
  } else {
    // For audio files, use a default audio icon
    return "";
  }
};

/**
 * Gets the duration of a media file using MediaBunny for videos
 */
export const getMediaDuration = async (
  file: File
): Promise<number | undefined> => {
  if (file.type.startsWith("video/")) {
    // For videos, try MediaBunny first for more reliable duration detection
    try {
      const metadata = await MediaBunnyThumbnailService.getVideoMetadata(file);
      return metadata.duration;
    } catch (error) {
      console.error("MediaBunny duration detection failed, falling back to HTML video element:", error);
      
      // Fallback to HTML video element
      return new Promise((resolve) => {
        const video = document.createElement("video");

        // Set timeout to handle cases where media loading hangs
        const timeoutId = setTimeout(() => {
          console.warn("Video duration detection timed out");
          URL.revokeObjectURL(video.src);
          resolve(undefined);
        }, 5000); // 5 second timeout

        video.preload = "metadata";
        video.onloadedmetadata = () => {
          clearTimeout(timeoutId);
          resolve(video.duration);
          URL.revokeObjectURL(video.src);
        };
        video.onerror = () => {
          clearTimeout(timeoutId);
          console.error("Error getting video duration");
          URL.revokeObjectURL(video.src);
          resolve(undefined);
        };
        video.src = URL.createObjectURL(file);
      });
    }
  } else if (file.type.startsWith("audio/")) {
    // For audio files, continue using HTML audio element
    return new Promise((resolve) => {
      const audio = document.createElement("audio");

      // Set timeout to handle cases where media loading hangs
      const timeoutId = setTimeout(() => {
        console.warn("Audio duration detection timed out");
        URL.revokeObjectURL(audio.src);
        resolve(undefined);
      }, 5000); // 5 second timeout

      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        resolve(audio.duration);
        URL.revokeObjectURL(audio.src);
      };
      audio.onerror = () => {
        clearTimeout(timeoutId);
        console.error("Error getting audio duration");
        URL.revokeObjectURL(audio.src);
        resolve(undefined);
      };
      audio.src = URL.createObjectURL(file);
    });
  }
  return undefined;
};

/**
 * Deletes a media file from the server
 */
export const deleteMediaFile = async (
  userId: string,
  filePath: string
): Promise<boolean> => {
  try {
    const response = await fetch("/api/media/delete", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, filePath }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete file");
    }

    return true;
  } catch (error) {
    console.error("Error deleting media file:", error);
    return false;
  }
};
